import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { DateTime } from 'luxon';
import { z } from 'zod';

extendZodWithOpenApi(z);

/**
 * Logger interface for provider integrations.
 *
 * This interface defines the logging contract that ProviderLogger implements.
 * It provides branded, prefixed logging for all provider operations with
 * consistent context (job_id, connection_id, provider).
 *
 * @remarks
 * All methods follow winston-compatible signatures: (message: string, metadata?: unknown)
 * The implementation (ProviderLogger) adds automatic prefixing, metrics tracking,
 * and HTTP dump capabilities.
 */
export interface ProviderLoggerInterface {
  error(message: string, metadata?: unknown): void;
  warn(message: string, metadata?: unknown): void;
  info(message: string, metadata?: unknown): void;
  http(message: string, metadata?: unknown): void;
  verbose(message: string, metadata?: unknown): void;
  debug(message: string, metadata?: unknown): void;
  silly(message: string, metadata?: unknown): void;
}

/** The specific type for Memory/Group/Timely Classic ID references */
export type VariousMemoryIdRefs =
  // Timeline legacy
  | `${number}`
  // Timeline new
  | `group_tic_${number}`
  | `group_timely_${number}`
  // List view
  | `tool_tic_${number}`
  | `context_tic_${number}`
  | `title_tic_${number}`
  | `tool_timely_${number}`
  | `context_timely_${number}`
  | `title_timely_${number}`;

/**
 * Schema for Memory/Group/Timely Classic ID references
 *
 * This schema uses z.coerce.string() as the base type to ensure proper OpenAPI export
 * and automatic coercion of numbers to strings, then validates the format using regex.
 */
export const VariousMemoryIdRefsSchema = z.coerce
  .string() // This will automatically convert numbers to strings
  .refine(
    val => {
      // Fast path: if it's a plain number, accept it immediately
      if (/^\d+$/.test(val)) return true;

      // Fast check using a single regex for all prefixed patterns
      return /^(group|tool|context|title)_(tic|timely)_\d+$/.test(val);
    },
    {
      message: 'Must be a valid reference format, see `VariousMemoryIdRefs` type',
    }
  )
  // Type assertion to preserve the template literal type
  .transform(val => val as VariousMemoryIdRefs)
  .openapi({
    description: 'Memory/Group/Timely Classic `id` references; real or virtual',
    example: `tic_group_123`,
    type: 'string',
  });

export const YYYYMMDDSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .openapi({
    example: '2024-02-06',
    description: 'YYYY-MM-DD relative to the users timezone',
  });

/**
 * Strict boolean parsing for query params.
 *
 * @remarks
 * `z.coerce.boolean()` treats any non-empty string (including `"false"`) as `true`,
 * which is not what we want for URL query parameters.
 */
export const QueryBooleanSchema = z.preprocess(value => {
  const normalized = Array.isArray(value) ? value[0] : value;

  if (normalized === 'true' || normalized === true || normalized === 1 || normalized === '1') {
    return true;
  }

  if (normalized === 'false' || normalized === false || normalized === 0 || normalized === '0') {
    return false;
  }

  return normalized;
}, z.boolean());

export const GenericUUID = z
  .string()
  .uuid()
  .openapi({ description: 'UUID identifier', example: '123e4567-e89b-12d3-a456-433114174000' });

export const GenericIdentityUUID = z
  .string()
  .uuid()
  .openapi({ description: 'The `identity.id`', example: '123e4567-e89b-12d3-a456-433114174000' });

/** TIC's `accounts.id` */
export const GenericAccountId = z
  .string()
  .uuid()
  .openapi({ description: 'TIC account ID', example: '123e4567-e89b-12d3-a456-433114174000' });

/** Timely `user.id` */
export const GenericTimelyUserId = z.coerce
  .number()
  .int()
  .min(1)
  .openapi({ description: 'The Timely `user.id`', example: 728755 });

/** Timely `account.id` */
export const GenericTimelyAccountId = z.coerce
  .number()
  .int()
  .min(1)
  .openapi({ description: 'The Timely `account.id`', example: 4301 });

export const GenericHTTPHeadersSchema = z
  .record(z.string(), z.union([z.string(), z.array(z.string()), z.undefined()]))
  .optional()
  .openapi({
    example: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer 123-ABC',
    },
  });

export const DateTimeTzSchema = z.string().datetime({ offset: true }).openapi({
  description: 'DateTime with timezone',
  example: '2023-10-20T15:00:00.000Z',
});

/**
 * Luxon's DateTime cannot be used for API interactions,
 * but it's used frequently in the client-side only code.
 */
export const LuxonDateTimeSchema = z
  .custom<DateTime<true>>(value => DateTime.isDateTime(value) && (value as DateTime).isValid, {
    message: 'Must be a valid DateTime object',
  })
  .describe('Luxon DateTime');

/** TIC `auth.user.id/profile.id` */
export const GenericUserID = z.string().uuid().openapi({
  description: 'User ID',
  example: '421e4567-e89b-12d3-a456-426614174000',
});

// Unfortunate workaround to get &param=true into boolean, ref https://github.com/colinhacks/zod/issues/1630
export const BooleanStrict = z
  .union([z.boolean(), z.literal('true'), z.literal('false')])
  .transform(value => value === true || value === 'true')
  .default(false)
  .openapi({ example: true });

/** Provider's connection.id */
export const GenericConnectionId = z.string().uuid().openapi({
  description: 'Connection ID',
  example: '421e4567-e89b-12d3-a456-426614174000',
});

/** Tool id, e.g `github` or `jira` */
export const GenericToolId = z.string().openapi({
  description: 'Tool ID',
  example: 'github',
});

/** The `resource_subscriptions.id` */
export const GenericSubscriptionId = z.string().uuid().openapi({
  example: '123e4567-e89b-12d3-a456-426614174000',
  description: 'Subscription ID',
});

/** The `resource_subscription_webhooks.id */
export const GenericSubscriptionWebhookId = z.string().uuid().openapi({
  example: '123e4567-e89b-12d3-a456-426614174000',
  description: 'Subscription Webhook ID',
});

/** The `resource.id` */
export const GenericResourceId = z.string().uuid().openapi({
  example: '123e4567-e89b-12d3-a456-426614174000',
  description: 'Resource ID',
});

/** The `notifications.id` */
export const GenericNotificationId = z.string().uuid().openapi({
  example: '123e4567-e89b-12d3-a456-426614174000',
  description: 'Notification ID',
});

/**
 * Re-usable Queries & Params
 * @todo This should probably be $refs, but I'm not sure how to do that with ts-rest
 * @todo This isn't technically a Zod schema, rename.
 */
export const URLParamsSchema = {
  operationId: z.string().openapi({ example: 'importOrganizations' }),
  provider: GenericToolId,
  nangoConnectionId: z.string().openapi({
    description: 'Finds the the `connection` via Nango reference',
    example: 'account_123e4567-e89b-12d3-a456-426614171000',
  }),
  connectionId: GenericConnectionId,
  limit: z.coerce.number().int().openapi({ example: 10, description: 'Limit # records' }),
  page: z.coerce.number().int().openapi({ example: 1, description: 'Page # records' }),
  // offset: z.coerce.number().openapi({ example: 0, description: 'Offset # records' }),
  subscriptionId: GenericSubscriptionId,
  thingId: z.coerce.number().openapi({ example: 1 }),
  resourceId: GenericResourceId,
  accountId: GenericAccountId,
  scope: z.enum(['personal', 'account', 'all']).openapi({ example: 'personal' }),
  toolId: GenericToolId,
  userId: GenericUserID,
  webhookId: GenericSubscriptionWebhookId,
  personalAccountId: GenericAccountId.describe('Personal account ID'),
  notificationId: GenericNotificationId,
};

export type ResponseMeta = z.infer<typeof ResponseMetaSchema>;

export const ResponseMetaSchema = z.object({
  count: z.number().describe('Number of records returned').default(0).openapi({ example: 100 }),
  totalCount: z.number().describe('Total number of records').default(0).openapi({ example: 233 }),
  message: z.string().optional().nullable().openapi({
    example: 'Requested range not satisfiable',
    description:
      'Description about the response, may include things like non-fatal errors made in the request (e.g, paginating past the last record)',
  }),
});

export const OKGeneric = z
  .object({
    code: z.number().optional(),
    message: z.string(),
  })
  .passthrough()
  .openapi({ example: { code: 200, message: 'All OK!' } });

export const ErrorGenericSchema = z
  .object({
    code: z.number().or(z.string()).optional(),
    message: z.string(),
  })
  .passthrough()
  .openapi({ example: { code: 500, message: 'Something went wrong' } });

export type ErrorGeneric = z.infer<typeof ErrorGenericSchema>;

/** AI Auth */
export const AISpecificHeadersSchema = z.object({
  authorization: z
    .string()
    .describe('Use the `INTERNAL_TIMELY_AI_API_KEY` key. Also required in localhost dev.')
    .openapi({ example: 'Bearer 123e4567-e89b-12d3-a456-426614174000' }),
});
